import { use, useMemo } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { type GQL } from "@binale-tech/shared";
import { type TTransactionData } from "@banks/types";
import { getBalance } from "@banks/scripts/helpers/getBalance";
import { getDateFormat } from "@banks/scripts/helpers/getDateFormat";
import { BanksContext, BankTransactionsContext } from "@domain/context/BanksContext";
import { type MetricStatus } from "./useGuVMetrics";

export interface BankSeriesPoint {
    /** YYYY-MM-DD */
    date: string;
    /** cents */
    balance: number;
}

export interface BankMetrics {
    status: MetricStatus;
    /** per-currency totals from getBalance (openingBalance + all transactions) */
    balances: Record<string, number>;
    eurBalance: number;
    /** trailing 30-day EUR balance, one point per day */
    series: BankSeriesPoint[];
}

export const computeBankBalanceSeries = (
    banks: GQL.IBank[],
    transactionsData: Record<string, TTransactionData>,
    today: Dayjs,
    days = 30,
): BankSeriesPoint[] => {
    const start = today.subtract(days - 1, "day").startOf("day");
    const endOfToday = today.endOf("day");
    let opening = 0;
    banks.forEach((bank) => {
        if (String(bank.bankCurrency || "EUR") !== "EUR") {
            return;
        }
        opening += bank.openingBalance ?? 0;
    });
    const perDay = new Map<string, number>();
    Object.values(transactionsData).forEach((txMap) => {
        Object.values(txMap || {}).forEach((tx) => {
            if (String(tx.currencyCode || "EUR") !== "EUR") {
                return;
            }
            if (!tx.bookingDate || !tx.bookingDate.length) {
                return;
            }
            let day: Dayjs;
            try {
                day = dayjs(tx.bookingDate, getDateFormat(tx.bookingDate));
            } catch {
                return; // unparseable booking date - mirror the banks module's defensive filtering
            }
            if (!day.isValid() || day.isAfter(endOfToday)) {
                return;
            }
            if (day.isBefore(start)) {
                opening += tx.amount ?? 0;
                return;
            }
            const key = day.format("YYYY-MM-DD");
            perDay.set(key, (perDay.get(key) ?? 0) + (tx.amount ?? 0));
        });
    });
    const points: BankSeriesPoint[] = [];
    let running = opening;
    for (let i = 0; i < days; i++) {
        const date = start.add(i, "day").format("YYYY-MM-DD");
        running += perDay.get(date) ?? 0;
        points.push({ date, balance: running });
    }
    return points;
};

export const useBankMetrics = (): BankMetrics => {
    const { banks } = use(BanksContext);
    const { transactionsData } = use(BankTransactionsContext);
    return useMemo(() => {
        // no isLoaded flag on the banks listener: an empty list shows as "empty" (block hidden)
        if (!banks.length) {
            return { status: "empty" as const, balances: {}, eurBalance: 0, series: [] as BankSeriesPoint[] };
        }
        const balances = getBalance(banks, transactionsData);
        return {
            status: "ready" as const,
            balances,
            eurBalance: balances.EUR ?? 0,
            series: computeBankBalanceSeries(banks, transactionsData, dayjs()),
        };
    }, [banks, transactionsData]);
};
